import {
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  Tooltip,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { DocGrantedUsersService } from '@affine/core/modules/permissions';
import { objectPermissionRoleLabel } from '@affine/core/modules/permissions/utils/object-permission-ui';
import { ShareInfoService } from '@affine/core/modules/share-doc';
import { UserFriendlyError } from '@affine/error';
import { DocRole } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { InformationIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useMemo, useRef, useState } from 'react';

import * as styles from './styles.css';

export const MembersPermission = ({ disabled }: { disabled?: boolean }) => {
  const t = useI18n();
  const shareInfoService = useService(ShareInfoService);
  const docGrantedUsersService = useService(DocGrantedUsersService);
  const shareInfo = useLiveData(shareInfoService.shareInfo.info$);
  const docDefaultRole = shareInfo?.defaultRole;
  const isDocDefaultRoleDirect = shareInfo?.defaultRoleDirect === true;
  const currentRoleName = useMemo(
    () => objectPermissionRoleLabel(t, docDefaultRole),
    [docDefaultRole, t]
  );
  const showTips =
    docDefaultRole === DocRole.Reader ||
    docDefaultRole === DocRole.Editor ||
    docDefaultRole === DocRole.None;
  // Absence of a direct default preserves any folder/group access. Do not load
  // every principal's effective-access graph just to render this one selector.
  const [pending, setPending] = useState(false);
  const busy = useRef(false);
  const locked = disabled || pending;
  const changePermission = useAsyncCallback(
    async (docRole: DocRole, options?: { inheritFromFolder?: boolean }) => {
      if (busy.current) return;
      busy.current = true;
      setPending(true);
      try {
        track.$.sharePanel.$.modifyDocDefaultRole({
          role: docRole,
        });
        await docGrantedUsersService.updateDocDefaultRole(docRole, options);
        await shareInfoService.shareInfo.waitForRevalidation();
      } catch (error) {
        const err = UserFriendlyError.fromAny(error);
        notify.error({
          title: err.name,
          message: err.message,
        });
      } finally {
        busy.current = false;
        setPending(false);
      }
    },
    [docGrantedUsersService, shareInfoService.shareInfo]
  );

  const selectManage = useCallback(() => {
    changePermission(DocRole.Manager);
  }, [changePermission]);

  const selectEdit = useCallback(() => {
    changePermission(DocRole.Editor);
  }, [changePermission]);

  const selectRead = useCallback(() => {
    changePermission(DocRole.Reader);
  }, [changePermission]);

  const selectNone = useCallback(() => {
    changePermission(DocRole.None);
  }, [changePermission]);

  const selectInherited = useCallback(() => {
    changePermission(DocRole.None, { inheritFromFolder: true });
  }, [changePermission]);

  const showFolderDerivedAccessHint =
    docDefaultRole === DocRole.None && !isDocDefaultRoleDirect;
  const displayedRoleName = showFolderDerivedAccessHint
    ? t['com.streamlain.permissions.review.inherited']()
    : currentRoleName;

  return (
    <div>
      <div className={styles.rowContainerStyle}>
        <div className={styles.labelStyle}>
          {t['com.streamlain.share-menu.option.permission.label']()}
        </div>
        {locked ? (
          <div className={clsx(styles.menuTriggerStyle, 'disable')}>
            <div className={styles.menuTriggerText}>
              {showTips ? <Tips disable={locked} /> : null} {displayedRoleName}
            </div>
          </div>
        ) : (
          <Menu
            contentOptions={{
              align: 'end',
            }}
            items={
              <>
                <MenuItem
                  onSelect={selectManage}
                  selected={docDefaultRole === DocRole.Manager}
                >
                  <div className={styles.publicItemRowStyle}>
                    {objectPermissionRoleLabel(t, DocRole.Manager)}
                  </div>
                </MenuItem>
                <MenuItem
                  onSelect={selectEdit}
                  selected={docDefaultRole === DocRole.Editor}
                >
                  <div className={styles.publicItemRowStyle}>
                    {objectPermissionRoleLabel(t, DocRole.Editor)}
                  </div>
                </MenuItem>
                <MenuItem
                  onSelect={selectRead}
                  selected={docDefaultRole === DocRole.Reader}
                >
                  <div className={styles.publicItemRowStyle}>
                    {objectPermissionRoleLabel(t, DocRole.Reader)}
                  </div>
                </MenuItem>
                <MenuItem
                  onSelect={selectNone}
                  selected={
                    docDefaultRole === DocRole.None && isDocDefaultRoleDirect
                  }
                >
                  <div className={styles.publicItemRowStyle}>
                    {objectPermissionRoleLabel(t, DocRole.None)}
                  </div>
                </MenuItem>
                {
                  <MenuItem
                    onSelect={selectInherited}
                    selected={
                      docDefaultRole === DocRole.None && !isDocDefaultRoleDirect
                    }
                  >
                    <div className={styles.publicItemRowStyle}>
                      {t['com.streamlain.permissions.review.inherited']()}
                    </div>
                  </MenuItem>
                }
              </>
            }
          >
            <MenuTrigger
              className={styles.menuTriggerStyle}
              variant="plain"
              suffixClassName={styles.suffixClassName}
              contentStyle={{
                width: '100%',
              }}
              prefix={showTips ? <Tips /> : undefined}
            >
              {displayedRoleName}
            </MenuTrigger>
          </Menu>
        )}
      </div>
      {showFolderDerivedAccessHint ? (
        <div className={styles.folderDerivedAccessNote}>
          {t['com.streamlain.permissions.review.inherited-note']()}
        </div>
      ) : null}
    </div>
  );
};

export const Tips = ({ disable }: { disable?: boolean }) => {
  const t = useI18n();

  return (
    <Tooltip content={t['com.streamlain.share-menu.option.permission.tips']()}>
      <InformationIcon
        className={clsx(styles.informationIcon, {
          disable: disable,
        })}
      />
    </Tooltip>
  );
};
